import * as Api from "@modules/api";
import { handleDispatchResult } from "@utils/helpers/handleDispatchResult";

import { store } from "../store";
import { ContentActions } from "./slices";
import {
  Content,
  GetContentEntityItemsByIdInput,
  ItemContentOutputWithProgress,
  SearchTrackedInput,
} from "./types";

export const clearContentState = () => {
  store.dispatch(ContentActions.clear());
};

export const clearSelectedPlayerContent = (content?: Partial<Content>) => {
  store.dispatch(ContentActions.clearSelectedPlayerContent(content));
};

export const clearNextContent = () => {
  store.dispatch(ContentActions.clearNextContent());
};

export const clearSearchTracked = () => {
  store.dispatch(ContentActions.clearSearchTracked());
};

export const setSelectedPlayerContent = (content: Content) => {
  store.dispatch(ContentActions.setSelectedPlayerContent(content));
};

export const setPlayingChapterId = (id: string | undefined) => {
  store.dispatch(ContentActions.setPlayingChapterId(id));
};

export const setFirstPlayingChapterId = (id: string | undefined) => {
  store.dispatch(ContentActions.setFirstPlayingChapterId(id));
};

export const setForcedPlayingChapterId = (id: string | undefined) => {
  store.dispatch(ContentActions.setForcedPlayingChapterId(id));
};

export const setIsCurrentChapterEnded = (isEnded: boolean) => {
  store.dispatch(ContentActions.setIsCurrentChapterEnded(isEnded));
};

export const togglePlayer = (isShown?: boolean) => {
  store.dispatch(ContentActions.togglePlayer(isShown));
};

export const changeLoading = (isLoading: boolean) => {
  store.dispatch(ContentActions.changeLoading(isLoading));
};

export const changeIsAutoStart = (isAutoStart: boolean) => {
  store.dispatch(ContentActions.changeIsAutoStart(isAutoStart));
};

export const setDuration = (duration: number) => {
  store.dispatch(ContentActions.setDuration(duration));
};

export const play = () => {
  store.dispatch(ContentActions.play());
};

export const pause = () => {
  store.dispatch(ContentActions.pause());
};

export const seek = (time: number) => {
  store.dispatch(ContentActions.seek(time));
};

export const changeVolume = (volume: number) => {
  store.dispatch(ContentActions.changeVolume(volume));
};

export const changePlaybackRate = (playbackRate: number) => {
  store.dispatch(ContentActions.changePlaybackRate(playbackRate));
};

export const getSelectedPlayerContentEntityItemsById = async (
  body: GetContentEntityItemsByIdInput,
  onSuccess?: (data: ItemContentOutputWithProgress[]) => void | Promise<void>
) => {
  const res = await store.dispatch(
    ContentActions.getSelectedPlayerContentEntityItemsByIdAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const getContentEntityItemsById = async (
  body: GetContentEntityItemsByIdInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(
    ContentActions.getContentEntityItemsByIdAsync(body)
  );

  return handleDispatchResult(res, onSuccess);
};

export const addEvent = async (
  body: Api.Server.AddSessionEventInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.addEventAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const getInterests = async (onSuccess?: () => void | Promise<void>) => {
  const res = await store.dispatch(ContentActions.getInterestsAsync());

  return handleDispatchResult(res, onSuccess);
};

export const setInterests = async (
  body: Api.Server.SetReaderInterestsInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.setInterestsAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const addBookmark = async (
  body: Api.Server.BookmarkContentEntityInput,
  onFailure?: () => void | Promise<void>,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.addBookmarkAsync(body));

  return handleDispatchResult(res, onSuccess, onFailure);
};

export const removeBookmark = async (
  body: Api.Server.UnBookmarkContentEntityInput,
  onFailure?: () => void | Promise<void>,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.removeBookmarkAsync(body));

  return handleDispatchResult(res, onSuccess, onFailure);
};

export const getHighlights = async (
  id: string | undefined,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.getHighlightsAsync(id));

  return handleDispatchResult(res, onSuccess);
};

export const createHighlight = async (
  body: Api.Server.CreateHighlightInput,
  onFailure?: () => void | Promise<void>,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.createHighlightAsync(body));

  return handleDispatchResult(res, onSuccess, onFailure);
};

export const removeHighlight = async (
  id: number | undefined,
  onFailure?: () => void | Promise<void>,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.removeHighlightAsync(id));

  return handleDispatchResult(res, onSuccess, onFailure);
};

export const getDynamicLink = async (
  id: string,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.getDynamicLinkAsync(id));

  return handleDispatchResult(res, onSuccess);
};

export const getNextContent = async (
  body: Api.Server.GetNextContentEntityInput,
  onSuccess?: (content: Api.Server.ContentEntityOutput) => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.getNextContentAsync(body));

  return handleDispatchResult(res, onSuccess);
};

export const searchTracked = async (
  body: SearchTrackedInput,
  onSuccess?: () => void | Promise<void>
) => {
  const res = await store.dispatch(ContentActions.searchTrackedAsync(body));

  return handleDispatchResult(res, onSuccess);
};
